import { initAccordion } from './components/accordion';
import { initSelectDropdowns } from './components/selectdropdown';
import { initTabs } from './components/tab';
import { addToCartProductPageEvent } from './GA4/addToCartProductPage';
import { viewItemProductPageEvent } from './GA4/viewItem';
import {
  initPurchaseOptions,
  setPagePrices,
  initVariantOptions,
  initQuantityOptions,
} from './utils/productpurchase';
import { validateEmailInput } from './utils/formValidation';
import { addToastListener } from './components/toast';
import { selectRelatedProduct } from './GA4/selectRelatedProduct';

document.addEventListener('DOMContentLoaded', () => {
  initTabs();
  initAccordion();
  initSelectDropdowns();
  applyStockNotificationValidation();
  addToastListener();
  makeButtonSticky();
  initProductPagePrices();
  setDeliveryCountdown();

  // GA4 Events
  document.addEventListener('gaLoaded', () => {
    viewItemProductPageEvent();
    addToCartProductPageEvent();
    selectRelatedProduct();
  });
});

function applyStockNotificationValidation() {
  const input = document.getElementById(
    'stock-notify-email',
  ) as HTMLInputElement | null;
  const error = document.getElementById(
    'stock-notify-error',
  ) as HTMLElement | null;
  const submitButton = document.getElementById(
    'stock-notify-submit',
  ) as HTMLButtonElement | null;

  if (!input || !error) return;
  let inputFocused = false;

  input.addEventListener('blur', () => {
    if (!inputFocused) {
      input.addEventListener('input', () => {
        validateEmailInput(input, error);
      });
      inputFocused = true;
    }
  });

  submitButton?.addEventListener('click', () => {
    validateEmailInput(input, error);
  });
}

function makeButtonSticky() {
  const button = document.getElementById(
    'add-to-cart-button',
  ) as HTMLButtonElement | null;
  const stickyButton = document.getElementById(
    'sticky-add-to-cart-button',
  ) as HTMLButtonElement | null;
  const cookieBannerElement = document.getElementById('cookieConsent');
  const relatedProductsSection = document.getElementById('related-products');
  const footer = document.querySelector('footer');
  const buttonHeight = button?.offsetHeight;
  let cookieBannerExists = cookieBannerElement !== null;
  let isDesktopView = window.innerWidth >= 768;
  let sectionInView = false;

  if (!button || !stickyButton) {
    console.error('Cannot find the add to cart buttons');
    return;
  }
  const showStickyButton = () => {
    stickyButton.classList.remove('hidden');
    const cookieBannerHeight = cookieBannerElement
      ? cookieBannerElement.offsetHeight
      : 135; // 135 is the tallest the cookie banner across all viewports
    const bottom = cookieBannerExists ? cookieBannerHeight + 8 : 8;

    setTimeout(() => {
      stickyButton.style.bottom = `${bottom}px`;
    }, 50);
  };

  const hideStickyButton = () => {
    stickyButton.style.bottom = `-${buttonHeight}px`;
    setTimeout(() => {
      stickyButton.classList.add('hidden');
    }, 150);
  };

  if ('IntersectionObserver' in window) {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          sectionInView = entry.isIntersecting;
          isDesktopView = window.innerWidth >= 768;
          if (!sectionInView && !isDesktopView) {
            showStickyButton();
          } else {
            sectionInView = true;
            hideStickyButton();
          }
        });
      },
      { threshold: 0.0 },
    );
    observer.observe(button);
    if (relatedProductsSection) {
      observer.observe(relatedProductsSection);
    } else if (footer) {
      observer.observe(footer);
    }
  }

  // Hide the sticky button on desktop view
  window.addEventListener('resize', () => {
    isDesktopView = window.innerWidth >= 768;
    if (isDesktopView || sectionInView) {
      hideStickyButton();
    } else {
      showStickyButton();
    }
  });

  // Move the sticky button when the cookie banner is removed
  document.addEventListener('cookieConsent', () => {
    cookieBannerExists = false;

    if (!sectionInView && !isDesktopView) {
      showStickyButton();
    } else {
      sectionInView = true;
      hideStickyButton();
    }
  });
}

function initProductPagePrices() {
  // ----------------------- Get the number of products in the cart -----------------------

  const totalPriceElement =
    document.querySelector<HTMLButtonElement>('[data-price-total]');

  const priceJsonString = totalPriceElement?.dataset.priceJson;
  const priceJson: VariantPrices = priceJsonString
    ? JSON.parse(priceJsonString)
    : {};
  initPurchaseOptions(priceJson);
  initQuantityOptions(priceJson);
  initVariantOptions(priceJson);
}

function setDeliveryCountdown() {
  document.addEventListener('cutoff-time', (event) => {
    const timerElement = document.getElementById('shipping-delivery-countdown');
    if (!timerElement) return;
    let animating = false;

    const e = event as CustomEvent;
    const timeString = e.detail.cutoffTime; // Expected format: "16:00:00"
    if (!timeString) return;

    const updateCountdown = (cutoffTime: Date) => {
      const intervalId = setInterval(() => {
        const now = new Date();
        let timeRemaining = Math.max(0, cutoffTime.getTime() - now.getTime());
        const hours = Math.floor(timeRemaining / (1000 * 60 * 60));
        timeRemaining %= 1000 * 60 * 60;
        const minutes = Math.floor(timeRemaining / (1000 * 60));
        timeRemaining %= 1000 * 60;
        const seconds = Math.floor(timeRemaining / 1000);

        timerElement.textContent =
          hours > 1
            ? `${hours}h ${minutes}m ${seconds}s`
            : `${minutes}m ${seconds}s`;
      }, 1000);
    };

    // Get today's date and merge with the provided time
    const today = new Date();
    const [hours, minutes, seconds] = timeString.split(':').map(Number);
    const cutoffTime = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate(),
      hours,
      minutes,
      seconds,
    );
    const pastTodaysCutoffTime = cutoffTime < today;
    if (!cutoffTime) return;

    if (pastTodaysCutoffTime) {
      cutoffTime.setDate(cutoffTime.getDate() + 1);
    }

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            animating = entry.isIntersecting;
            if (animating) {
              requestAnimationFrame(() => {
                updateCountdown(cutoffTime);
              });
            } else animating = false;
          });
        },
        { threshold: 0.0 },
      );

      observer.observe(timerElement);
    } else {
      document.addEventListener('scroll', function () {
        const rect = timerElement.getBoundingClientRect();
        const inView = rect.top < window.innerHeight && rect.bottom >= 0;

        if (inView && !animating) {
          animating = true;
          requestAnimationFrame(() => {
            updateCountdown(cutoffTime);
          });
        } else if (!inView && animating) {
          animating = false;
        }
      });
    }
  });
}
